import { EventType, KnockautEvent } from './event'

export interface SymconCategory {
  parentID?: number
  name: string
}

export interface BaseSymconObject {
  position: number
  parentID: number
  disabled: boolean
  hidden: boolean
  icon: string
  ident: string
  info: string
  name: string
  readOnly: boolean
  summary: string
  type: SymconObjectType
}

// TODO: Split into different Types for Event, Variable
/**
 * The symcon objects as they are returned from the controller
 */
export interface SymconObject extends BaseSymconObject {
  data?: SymconObjectData
}

export interface SymconObjectData {
  lastChange?: number

  moduleID?: string
  moduleName?: string
  moduleType?: number

  connectionID?: number
  configuration?: {
    EnableRequestLicenseData?: boolean
    EnableSendUsageDat?: boolean
    LastCheckedVersion?: string
    [key: string]: any
  }
  attributes?: any

  targetID?: number // This is present for LINK type
  profile?: string // This is present for VARIABLE type
  customProfile?: string // This is present for VARIABLE type
  value?: boolean | number // This is present for VARIABLE type
  type?: SymconVariableType // This is present for VARIABLE & EVENT type
  lastUpdate?: number // This is present for VARIABLE type

  active?: boolean // Present for EVENT type

  status?: number // Archive
}

// This object is for use within Knockaut Tab (and especially AgGrid)
export interface KnockautObject extends SymconObject {
  id: number
  text: string
  parentPath: number[]
  [key: string]: any
}

export interface FlowScriptData {
  events: KnockautEvent[]
  links: number[]
  script: SymconScriptData
  scriptContent: SymconFlowScriptData
}

export interface SymconScriptData {
  ScriptCRC: string
  ScriptExecuted: number
  ScriptFile: string
  ScriptID: number
  ScriptIsBroken: boolean
  ScriptSize: number
  ScriptType: SymconScriptType
  ScriptUpdated: number
}

export interface SymconFlowScriptDataAction {
  action: SymconFlowScriptAction
  actionID: string
  parameters: {
    [param: string]: any
  }
}

export interface SymconFlowScriptData {
  actions: SymconFlowScriptDataAction[]
  strategy: SymconFlowScriptStrategy
}

export interface SymconFlowScriptStrategy {
  error: number
  errorRetries: number
  executions: number
}

// Raw data coming from Knockaut API
export interface RawKnockautProfile {
  associations: {
    color: number
    icon: string
    name: string
    value: number
  }[]
  digits: number
  icon: string
  maxValue: number
  minValue: number
  prefix: string
  readOnly: boolean
  stepSize: number
  suffix: string
  type: number
}

export interface ResolvedKnockautAction {
  index: number // Index of action in list of actions, starting with 0
  action: KnockautAction
  actionID: string // Action GUID
  actionText: string
  parameters: {
    TARGET: number
    VALUE?: any
    [param: string]: any
  }
  targetText: string
}

export interface SymconFlowScriptAction {
  id: string
  caption: string
  form: any[]
  action: string | string[]
  category: string
  description: string
  locale: {
    [lang: string]: {
      [key: string]: string
    }
  }
  restrictions?: {
    objectType?: SymconObjectType[]
    requestAction?: boolean
    variableType?: SymconVariableType[]
  }
}

// Raw data coming from Knockaut API
// https://www.symcon.de/de/service/dokumentation/entwicklerbereich/sdk-tools/sdk-php/aktionen/
export interface KnockautAction {
  id: string
  caption: string
  form: string[] | string | { caption: string; name: string; type: string }[]
  action: string[] | string
  priority?: number
  caregory?: SymconActionCategory
  restrictions?: SymconActionCategory
  locale?: any // contains local translations from the module
  format?: string // Formats in ICU-Syntax. (to use with resolveFormatString() in utils.ts )
  description?: string
  readable?: string[] | string
  deprecated?: string[] | string
}

export enum SymconActionCategory {
  TARGET = 'target',
  MATH = 'math',
  SET = 'set',
  SWITCH = 'switch',
  EXPERT = 'expert',
  OTHER = 'other',
}

export interface SymconRestrictions {
  environment?: SymconEnvironment
  general?: boolean
  objectType?: SymconObjectType[]
  profileIsEnum?: boolean
  variableType?: SymconVariableType[]
  profilesInclude?: string[] // ProfileNames f.ex. '~HexColor'
  profilesExclude?: string[] // ProfileNames f.ex. '~HexColor'
  includeEnvironments?: SymconEnvironment[]
  excludeEnvironments?: SymconEnvironment[]
  profileIsPercentage?: boolean
  requestAction?: boolean
  writable?: boolean
  eventType?: EventType
  hasInstanceFunctions?: boolean
  moduleID?: string[] // GUIDs von Modulen
  hasActionStatusVariables?: boolean
  scriptType?: SymconScriptType
}

export enum SymconEnvironment {
  DEFAULT = 'Default',
  TEST_COMMANDS = 'TestCommands',
  EVENT_TRIGGER = 'EventTrigger',
  EVENT_CYCLIC = 'EventCyclic',
  EVENT_SCHEDULE = 'EventSchedule',
  FLOW_SCRIPT = 'FlowScript',
  SCRIPT_EDITOR = 'ScriptEditor',
  // add additional custom environments here
}

export enum KnockautConfigurationType {
  // TODO: Add more
  BUTTON = 'Button',
  LABEL = 'Label',
  LIST = 'List',
  ROW_LAYOUT = 'RowLayout',
  CHECKBOX = 'CheckBox',
  NUMBER_SPINNER = 'NumberSpinner',
  PASSWORD_TEXT_BOX = 'PasswordTextBox',
  VALIDATION_TEXT_BOX = 'ValidationTextBox',
  SELECT = 'Select',
  SELECT_VARIABLE = 'SelectVariable',
  SELECT_INSTANCE = 'SelectInstance',
  SELECT_CONDITION = 'SelectCondition',
}

export interface KnockautConfigurationElement {
  caption?: string
  name?: string
  options?: {
    label: string
    value: number
  }[]
  items?: any[]
  type: KnockautConfigurationType
}

export interface KnockautConfigurationStatus {
  code: number
  icon: string
  caption: string
}

export interface KnockautConfigurationForm {
  actions?: KnockautConfigurationElement[]
  elements?: KnockautConfigurationElement[]
  status?: KnockautConfigurationStatus[]
  translations?: {
    de?: Record<string, string>
  }
}

export interface RawKnockautSnapshot {
  objects: Record<string, KnockautObject>
  compatibility: {
    date: number
    version: string
  }
  options: {
    ServerSecurity: boolean
    ThreadCount: number
  }
  profiles: Record<string, RawKnockautProfile>
  timestamp: number
  timezone: string
}

export interface KnockautInstance extends KnockautObject {
  children: KnockautInstance[] | null
}

export const DEFAULT_KNOCKAUT_INSTANCE: KnockautInstance = {
  id: null,
  children: [],
  parentPath: [],
  disabled: false,
  hidden: false,
  icon: '',
  text: '',
  opened: false,
  position: null,
  ident: '',
  info: '',
  name: '',
  parentID: null,
  readOnly: false,
  summary: '',
  type: null,
}

export enum SymconObjectType {
  CATEGORY = 0,
  INSTANCE = 1,
  VARIABLE = 2,
  SCRIPT = 3,
  EVENT = 4,
  MEDIA = 5,
  LINK = 6,
}

export enum SymconScriptType {
  PHP = 0,
  FLOW = 1,
}

export enum SymconModuleType {
  CORE = 0,
  I_O = 1,
  SPLITTER = 2,
  DEVICE = 3,
  CONFIGURATOR = 4,
  DISCOVERY = 5,
  VISUALIZER = 6,
}

export enum SymconVariableType {
  BOOLEAN = 0,
  INTEGER = 1,
  FLOAT = 2,
  STRING = 3,
}

// Variables can be of type string, number or boolean
export type SymconVariableValueType = string | number | boolean

export interface CreateSymconObjectModel {
  parentID: number
  name: string
  type: SymconObjectType
}

export interface SymconLocation {
  latitude: number
  longitude: number
}

export class SymconConnectionError extends Error {
  constructor(message?: string) {
    super(message) // 'Error' breaks prototype chain here
    Object.setPrototypeOf(this, new.target.prototype) // restore prototype chain
  }
}

// See https://www.symcon.de/service/dokumentation/modulreferenz/system-informationen/sys-getnetworkinfo/
export interface SymconNetworkInfo {
  Description: string
  IP: string
  InTotal: number
  InterfaceIndex: number
  MAC: string
  OutTotal: number
  Speed: number
}

export interface SymconBaseBranchInfo {
  ModuleTime: number
  ModuleCommit: string
  ModuleBranch: string
  ModuleURL: string
}

export interface SymconModuleBranchInfo extends SymconBaseBranchInfo {
  ModuleAvailableBranches: SymconBaseBranchInfo[]
  LibraryID: string
  LibraryModuleName: string
}

export const EVENTS_FOLDER_NAME = 'Ereignisse'
export const SCRIPTS_FOLDER_NAME = 'Skripte'

export const BRELAG_DOMINO_SWISS_MODULE_URL =
  'https://github.com/Brelag-Schweiz-AG/KnockautTitan_Dominoswiss'

// Module Names
export const BRELAG_CONFIGURATOR_MODULE_NAME = 'BrelagConfigurator'
export const KNOCKAUT_CONFIGURATOR_MODULE_NAME = 'KnockAut Configurator'
export const DASHBOARD_CONFIGURATOR_MODULE_NAME = 'WebFront Configurator'

// Library GUIDS
export const KNOCKAUT_APP_LIBRARY_GUID =
  '{AA15BB89-F286-0E10-0A1D-14A344215CCA}'
export const BRELAG_DOMINOSWISS_LIBRARY_GUID =
  '{7E2FBCEA-D9A4-4710-B56D-860748B01B17}'
export const KNOCKAUT_ANALYTICS_LIBRARY_GUID =
  '{4BE0B0C6-01AB-220E-9993-A2FB5232CD7C}'
export const KNOCKAUT_X_LIBRARY_GUID = '{CA7DE84E-FD60-B082-F83E-646D2C59A3E4}'

// Module IDS
export const KNOCKAUT_CONFIGURATOR_MODULE_ID =
  '{0361A1D1-089F-143B-43AF-3814D2DF0117}'
export const BRELAG_CONFIGURATOR_MODULE_ID =
  '{7979B248-1D5C-4607-8F75-8CB50CD69E34}'
export const DASHBOARD_CONFIGURATOR_MODULE_ID =
  '{3565B1F2-8F7B-4311-A4B6-1BF1D868F39E}'
export const CONNECT_MODULE_ID = '{9486D575-BE8C-4ED8-B5B5-20930E26DE6F}'
export const BRELAG_DOMINO_SWISS_MODULE_ID =
  '{B8A5067A-AFC2-3798-FEDC-BCD02A45615E}'
export const ARCHIVE_CONTROL_MODULE_ID =
  '{43192F0B-135B-4CE7-A0A7-1475603F3060}'
export const UTIL_CONTROL_MODULE_ID = '{B69010EA-96D5-46DF-B885-24821B8C8DBD}'
export const LOCATION_MODULE_ID = '{45E97A63-F870-408A-B259-2933F7EABF74}'
export const DUMMY_MODULE_ID = '{485D0419-BE97-4548-AA9C-C083EB82E61E}'
export const KNOCKAUT_X_SPLITTER_GUID = '{7A84E4E4-920B-C4D9-5B0D-9B2A6409FC9B}'

// Action GUIDS
export const SYMCON_ACTION_EXECUTE_AUTOMATION =
  '{7938A5A2-0981-5FE0-BE6C-8AA610D654EB}' // Führe Automation aus (Ablaufplan)
